// Dashboard browser component for viewing Metabase reports

import { useState } from 'react';
import { Box, IconButton, HStack } from '@chakra-ui/react';
import { MdArrowBack } from "react-icons/md";
import { Report } from '@/lib/sdk/reports/types';
interface DashboardBrowserProps {
  report: Report;
  onClose: () => void;
}

/**
 * Component to display Metabase dashboards in an iframe with browser-like navigation
 */
export function DashboardBrowser({ report, onClose }: DashboardBrowserProps) {
  const [history, setHistory] = useState<string[]>([report.public_url]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      onClose();
    }
  };

  const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    // Track navigation history if URL changes
    const iframe = e.currentTarget;
    try {
      const iframeUrl = iframe.contentWindow?.location.href;
      if (iframeUrl && iframeUrl !== history[currentIndex]) {
        const newHistory = history.slice(0, currentIndex + 1);
        newHistory.push(iframeUrl);
        setHistory(newHistory);
        setCurrentIndex(newHistory.length - 1);
      }
    } catch (error) {
      // Cross-origin restrictions prevent accessing iframe URL
      console.log('Cannot access iframe URL due to CORS');
    }
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="bg-surface"
      zIndex={1000}
    >
      {/* Navigation Bar */}
      <HStack
        p={2}
        borderBottomWidth="1px"
        borderColor="border-default"
        bg="bg-muted"
        spacing={2}
      >
        <IconButton
          aria-label="Go back"
          icon={<MdArrowBack />}
          onClick={handleBack}
          size="sm"
        />
        <Box flex={1}>
          <Box
            px={3}
            py={1}
            bg="bg-surface"
            borderRadius="md"
            borderWidth="1px"
            fontSize="sm"
            color="gray.600"
            isTruncated
          >
            {report.title}
          </Box>
        </Box>
      </HStack>

      {/* Dashboard Content */}
      <Box h="calc(100vh - 50px)">
        <iframe
          src={history[currentIndex]}
          title={report.title}
          width="100%"
          height="100%"
          frameBorder="0"
          allowFullScreen
          onLoad={handleIframeLoad}
          style={{
            border: 'none',
            display: 'block',
          }}
        />
      </Box>
    </Box>
  );
}
